// Pilot local time is Asia/Kolkata. India has used a fixed +05:30 offset with
// no daylight saving since 1945, so a fixed offset is exact here. Timestamps
// are stored in UTC; local dates are 'YYYY-MM-DD' strings used for expiry,
// spend periods, billing periods and due dates.

export const PILOT_TIME_ZONE = 'Asia/Kolkata';
const OFFSET_MS = 330 * 60 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;

export type LocalDate = string; // 'YYYY-MM-DD'

function shift(d: Date): Date {
  return new Date(d.getTime() + OFFSET_MS);
}

export function localDate(d: Date): LocalDate {
  return shift(d).toISOString().slice(0, 10);
}

export function localMonth(d: Date): string {
  return localDate(d).slice(0, 7);
}

/** UTC instant of local midnight at the start of the given local date. */
export function startOfLocalDate(date: LocalDate): Date {
  assertLocalDate(date);
  return new Date(Date.parse(`${date}T00:00:00.000Z`) - OFFSET_MS);
}

/** UTC instant of the last millisecond of the given local date. */
export function endOfLocalDate(date: LocalDate): Date {
  return new Date(startOfLocalDate(date).getTime() + DAY_MS - 1);
}

export function endOfLocalDay(d: Date): Date {
  return endOfLocalDate(localDate(d));
}

export function addDays(date: LocalDate, days: number): LocalDate {
  assertLocalDate(date);
  return new Date(Date.parse(`${date}T00:00:00.000Z`) + days * DAY_MS).toISOString().slice(0, 10);
}

/** Whole days from a to b (positive when b is later). */
export function daysBetween(a: LocalDate, b: LocalDate): number {
  return Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / DAY_MS);
}

/** ISO weekday for a local date: 1 = Monday … 7 = Sunday. */
export function isoWeekday(date: LocalDate): number {
  const day = new Date(`${date}T00:00:00Z`).getUTCDay();
  return day === 0 ? 7 : day;
}

export function daysInMonth(year: number, month1: number): number {
  return new Date(Date.UTC(year, month1, 0)).getUTCDate();
}

export function monthStart(date: LocalDate): LocalDate {
  return `${date.slice(0, 7)}-01`;
}

export function addMonths(date: LocalDate, months: number, dayOfMonth?: number): LocalDate {
  const y = Number(date.slice(0, 4));
  const m = Number(date.slice(5, 7)) - 1 + months;
  const year = y + Math.floor(m / 12);
  const month1 = (((m % 12) + 12) % 12) + 1;
  const d = Math.min(dayOfMonth ?? Number(date.slice(8, 10)), daysInMonth(year, month1));
  return `${year}-${String(month1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

export function assertLocalDate(date: string): asserts date is LocalDate {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || Number.isNaN(Date.parse(`${date}T00:00:00Z`))) {
    throw new RangeError(`Invalid local date: ${date}`);
  }
}

export function isLocalDate(value: unknown): value is LocalDate {
  try {
    assertLocalDate(String(value));
    return true;
  } catch {
    return false;
  }
}

/** "5 Oct 2026, 4:05 pm" in pilot local time, without relying on Intl data. */
export function formatLocalDateTime(d: Date | string): string {
  const date = typeof d === 'string' ? new Date(d) : d;
  const s = shift(date);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const h = s.getUTCHours();
  const hh = h % 12 === 0 ? 12 : h % 12;
  const mm = String(s.getUTCMinutes()).padStart(2, '0');
  return `${s.getUTCDate()} ${months[s.getUTCMonth()]} ${s.getUTCFullYear()}, ${hh}:${mm} ${h < 12 ? 'am' : 'pm'}`;
}

export function formatLocalDate(date: LocalDate | Date | string): string {
  const ld = date instanceof Date ? localDate(date) : date.length > 10 ? localDate(new Date(date)) : date;
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${Number(ld.slice(8, 10))} ${months[Number(ld.slice(5, 7)) - 1]} ${ld.slice(0, 4)}`;
}
